"use client";

import { useState } from "react";
import { ThemeProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthGatewayProviders } from "@miadvg/auth-gateway/next-client";
import { ConfigProvider } from "@miadvg/config";
import { PostHogProvider } from "@miadvg/posthog";
import {
  RESURRECTION_STAFF_APP_SLUG,
  RESURRECTION_STAFF_AUTH_CALLBACK_PATH,
  RESURRECTION_STAFF_SIGN_OUT_PATH,
} from "@/lib/auth/staff-shared";
import { HotkeysProvider } from "@/app/providers/hotkeys-provider";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { WowGate } from "@/components/wow/wow-gate";
import { WowModeProvider } from "@/components/wow/wow-mode";
import { WowHearthstone } from "@/components/wow/hearthstone";

interface ProvidersProps {
  children: React.ReactNode;
}

/**
 * Client-side providers wrapper
 *
 * ConfigProvider: Fetches runtime config from /api/config
 * PostHogProvider: Initializes PostHog after config is ready
 *
 * Product events: `captureEvent`. Feature flags: `useServiceFlagEnabled` /
 * `createServiceFlagHooks` from `@miadvg/posthog`. Raw `posthog-js/react` only
 * when you need APIs this package does not wrap (e.g. `usePostHog`).
 */
export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <ConfigProvider>
          <PostHogProvider
            options={{
              api_host: "/ingest",
              ui_host: "https://us.i.posthog.com",
            }}
          >
            <AuthGatewayProviders
              staff={{
                appSlug: RESURRECTION_STAFF_APP_SLUG,
                callbackPath: RESURRECTION_STAFF_AUTH_CALLBACK_PATH,
                signOutPath: RESURRECTION_STAFF_SIGN_OUT_PATH,
              }}
              client={{
                sessionEndpointPath: "/api/client/session",
              }}
            >
              <WowModeProvider>
                <TooltipProvider>
                  <HotkeysProvider>
                    <WowGate>{children}</WowGate>
                    <WowHearthstone />
                  </HotkeysProvider>
                </TooltipProvider>
              </WowModeProvider>
              <Toaster />
            </AuthGatewayProviders>
          </PostHogProvider>
        </ConfigProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
